import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { View, Text, FlatList, TextInput, TouchableOpacity } from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { useResidentConversation, useResidentMessaging } from '@/hooks/residents/useMessages';
import { useMarkConversationAsReadMutation } from '@/store/api/admin/messagesApi';
import type { IChatWindowProps, IMessage, IFile } from '@/types/admin/messages';
import { FileUploadSection, UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { useAppSelector } from '@/store';
import styles from '@/components/residents/propertyChat/ResidentChatWindow.styles';
import { FileUploadBubble } from './FileUploadBubble';
import { MessageBubble } from './MessageBubble';
import { FileBubble } from './FileBubble';
import AttachmentMessageDesktop from '@/components/residents/propertyChat/AttachmentMessageDesktop';
import { groupMessages, type MessageGroup } from '@/utils/residents/messages/groupMessages';
import { useAutoScrollToBottom } from '@/hooks/residents/useAutoScrollToBottom';
import { PROPERTY_CHAT_FILE_UPLOAD_MAX_FILES } from '@/constants/residents/propertyChat';
import { useToast } from '@/contexts/admin/ToastContext';

export const ResidentChatWindow: React.FC<IChatWindowProps> = ({ conversationId }) => {
  const { conversation, isLoading, error } = useResidentConversation(conversationId);
  const { sendMessage, isLoading: isSending } = useResidentMessaging() as any;
  const [markAsRead] = useMarkConversationAsReadMutation();
  const authUserId = useAppSelector((state) => state.auth.user?.id);
  const { showError } = useToast();

  const [messageText, setMessageText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [fileUploadAnchor, setFileUploadAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const { listRef: flatListRef, isNearBottomRef, maybeScrollToBottom, scrollToBottom, handleScroll } = useAutoScrollToBottom<FlatList>();
  const messagesEndRef = useRef<View>(null);
  const paperclipButtonRef = useRef<View>(null);
  
  // Track if conversation has been marked as read to avoid duplicate calls
  const hasMarkedAsReadRef = useRef<number | null>(null);

  // Mark conversation as read when user has viewed messages
  // Deferred to ensure user actually sees the content, not just on load
  useEffect(() => {
    if (!conversationId || !conversation) return;
    
    // Reset tracking when conversation changes
    if (hasMarkedAsReadRef.current !== conversationId) {
      hasMarkedAsReadRef.current = null;
    }
    
    // Already marked this conversation as read
    if (hasMarkedAsReadRef.current === conversationId) return;
    
    // Mark as read after a short delay to ensure content is visible
    // This prevents marking as read before the user can actually see messages
    const timeoutId = setTimeout(() => {
      if (hasMarkedAsReadRef.current !== conversationId) {
        hasMarkedAsReadRef.current = conversationId;
        markAsRead(conversationId).catch(() => {
          // Silently fail - not critical, reset so we can retry
          hasMarkedAsReadRef.current = null;
        });
      }
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [conversationId, conversation, markAsRead]);

  // Force scroll to bottom - used when loading new conversation
  const forceScrollToBottom = useCallback((animated: boolean = true) => {
    scrollToBottom(animated);
  }, []);

  // Handle file upload dropdown - desktop style like resio-web
  const openFileUpload = useCallback(() => {
    if (paperclipButtonRef.current) {
      paperclipButtonRef.current.measureInWindow((x, y, width, height) => {
        setFileUploadAnchor({ x, y, w: width, h: height });
        setShowFileUpload(true);
      });
    }
  }, []);

  const closeFileUpload = useCallback(() => {
    setShowFileUpload(false);
    setFileUploadAnchor(null);
  }, []);

  // Handle file selection - add files and close dropdown
  const handleFilesChange = useCallback((newFiles: UploadFile[]) => {
    setAttachedFiles(newFiles);
    // Close dropdown after files are added (but not when removing files)
    if (newFiles.length > attachedFiles.length) {
      closeFileUpload();
    }
  }, [attachedFiles.length, closeFileUpload]);

  const messageGroups = useMemo(() =>
    groupMessages<IMessage>(conversation?.messages, {
      getHeaderDate: (msg) => new Date(msg.createdAt).toDateString(),
      getTimestamp: (msg) => msg.createdAt,
      sortDaysBy: 'dateKey',
    }),
  [conversation?.messages]);

  // Effect to handle scrolling when conversation loads or messages change
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (messageGroups.length > 0) {
      // Force scroll to bottom when conversation first loads or changes
      timeoutId = setTimeout(() => {
        forceScrollToBottom(true);
      }, 150); // Slightly longer delay to ensure content is rendered
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [messageGroups, forceScrollToBottom]);

  // Effect to handle scrolling when conversation ID changes (new conversation loaded)
  useEffect(() => {
    if (conversationId && messageGroups.length > 0) {
      // Reset scroll position when loading new conversation
      isNearBottomRef.current = true;
      setTimeout(() => {
        forceScrollToBottom(false); // No animation for immediate scroll
      }, 50);
    }
  }, [conversationId, forceScrollToBottom]);

  // keyboard-driven scroll handled via useAutoScrollToBottom

  const handleSendMessage = async () => {
    if (isSending || (!messageText.trim() && attachedFiles.length === 0)) return;
    
    // Build FormData consistently with mobile and backend expectations
    const formData = new FormData();
    formData.append('content', messageText.trim());
    formData.append('isAdmin', 'false');
    attachedFiles.forEach((file) => {
      if (file.file) {
        formData.append('files', file.file);
      } else if (file.uri) {
        formData.append('files', { uri: file.uri, type: file.type, name: file.name } as any);
      }
    });
    
    try {
      await sendMessage({ conversationId, body: formData }).unwrap();
      setMessageText('');
      setAttachedFiles([]);
      // Auto-scroll after sending - match resio-web behavior
      setTimeout(() => {
        forceScrollToBottom(false);
      }, 300);
    } catch (e) {
      console.error('Failed to send message:', e);
      const msg = (e as any)?.data?.message || (e as any)?.error || 'Failed to send message. Please try again.';
      showError(msg);
    }
  };

  const renderMessageGroup = ({ item }: { item: MessageGroup<IMessage> }) => (
    <View style={styles.messageGroup}>
      <View style={styles.dateHeader}>
        <Text style={styles.dateText}>{item.date}</Text>
      </View>
          {item.messages.map((message) => (
            <View key={message.id}>
              {message.content?.length > 0 && (
                <MessageBubble message={message} authUserId={authUserId} />
              )}
              <AttachmentMessageDesktop message={message} authUserId={authUserId} />
            </View>
          ))}
    </View>
  );

  // Enhanced form validation
  const isValidMessage = useMemo(() => {
    return messageText.trim().length > 0 || attachedFiles.length > 0;
  }, [messageText, attachedFiles]);

  const renderMessageInput = () => (
    <View style={styles.messageInputFooter}>
      {/* File attachments preview - match resio-web */}
      {attachedFiles.length > 0 && (
        <View style={styles.fileUploadPreview}>
          {attachedFiles.map((file, index) => (
            <FileUploadBubble 
              key={file.id || index} 
              file={file} 
              onDelete={() => setAttachedFiles((f) => f.filter((_, i) => i !== index))}
            />
          ))}
        </View>
      )}
      
      <View style={styles.inputRowContainer}>
        <View style={styles.inputRow}>
          {/* Paperclip icon */}
          <View ref={paperclipButtonRef} style={styles.paperclipButtonWrapper}>
            <TouchableOpacity 
              style={styles.paperclipButton} 
              onPress={openFileUpload}
            >
              <ADMIN_SVG_COMPONENTS.PAPERCLIP 
                width={24} 
                height={24} 
                color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
              />
            </TouchableOpacity>
          </View>

          {/* Message input - styled like resio-web TextField */}
          <TextInput
            style={styles.messageInput}
            value={messageText}
            onChangeText={setMessageText}
            placeholder="Your message..."
            placeholderTextColor={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
            multiline
            maxLength={1000}
            textAlignVertical="top"
            onFocus={() => setTimeout(() => forceScrollToBottom(false), 300)}
          />
          
          {/* Send button */}
          <View style={[
            styles.sendButtonContainer,
            isValidMessage && styles.sendButtonContainerActive
          ]}>
            <TouchableOpacity
              style={styles.sendButton}
              onPress={handleSendMessage}
              disabled={isSending || !isValidMessage}
            >
              <Text style={styles.sendButtonText}>Send</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );

  // moved to FileUploadBubble.tsx

  if (isLoading) {
    return (
      <LoadingSpinner 
        message="Loading conversation..." 
        size="large" 
        showMessage={true}
      />
    );
  }
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load conversation</Text>
      </View>
    );
  }
  if (!conversation) {
    return (
      <View style={styles.emptyContainer}>
        <RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX width={64} height={64} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE}/>
        <Text style={styles.emptyText}>Select a conversation to start messaging</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Messages List with proper scroll handling */}
      <View style={styles.messagesContainer}>
        <FlatList
          ref={flatListRef}
          data={messageGroups}
          renderItem={renderMessageGroup}
          keyExtractor={(item) => `${item.date}:${item.messages.length}`}
          style={styles.messagesList}
          contentContainerStyle={styles.messagesContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="none"
          scrollEventThrottle={16}
          onScroll={(e) => handleScroll(e.nativeEvent.contentOffset.y, e.nativeEvent.layoutMeasurement.height, e.nativeEvent.contentSize.height, 24)}
          onContentSizeChange={() => maybeScrollToBottom(true)}
          onLayout={() => setTimeout(() => maybeScrollToBottom(true), 100)}
        />
        {/* Invisible div for scroll reference - like resio-web */}
        <View ref={messagesEndRef} style={{ height: 1 }} />
      </View>
      {renderMessageInput()}

      {/* Desktop-style file upload dropdown - matching resio-web */}
      <AnchoredDropdown
        isOpen={showFileUpload}
        anchor={fileUploadAnchor}
        width={480}
        maxHeight={600}
        onRequestClose={closeFileUpload}
      >
        <View style={styles.fileUploadDropdown}>
          <View style={styles.fileUploadDropdownHeader}>
            <Text style={styles.fileUploadDropdownTitle}>Add Attachments</Text>
          </View>
          <View style={styles.fileUploadDropdownContent}>
            <Text style={styles.fileUploadSubtitle}>Add files to your message</Text>
            <FileUploadSection
              title=""
              subtitle=""
              files={attachedFiles}
              onFilesChange={handleFilesChange}
              maxFiles={PROPERTY_CHAT_FILE_UPLOAD_MAX_FILES}
              allowedTypes={["image"]}
            />
          </View>
        </View>
      </AnchoredDropdown>
    </View>
  );
};

// styles moved to ResidentChatWindow.styles.ts

export default ResidentChatWindow;


